/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tree, Input, Button, Modal } from 'antd'
import { ItemNode } from '.'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import { setEditorCascadeItemsList } from '../../../../../actions/watermark'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'

const { TreeNode } = Tree

const ContentItem = ({ item, level, isOnlyOne, onChange, onAdd, onDelete }) => {
  // 多级选项输入框位于 antd <Tree> 的 title 内：每次 onChange 派发 redux 会让
  // cascadeItems memo 重算出全新深拷贝（节点对象身份全变）→ Tree 重渲染所有 title，
  // 在中文输入法合成途中打断 IME，拼音串被直接当最终值提交。
  // 改为：打字时只更新本地 state（不派发 redux、Tree 不重渲染、IME 不被打断），
  // 失焦时再把最终值提交给 redux。
  const [localValue, setLocalValue] = useState(item.content)
  // 外部变化（拖拽/批量导入/其它项提交导致的 content 变更）同步进来；
  // 打字期间不 dispatch、item.content 不变，故此 effect 不会在打字中触发、localValue 不被覆盖。
  useEffect(() => {
    setLocalValue(item.content)
  }, [item.content])

  return (
    <div className="cascade-item">
      <div className="input-wrapper">
        <Input
          className="cascade-item-input"
          placeholder="请输入..."
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={() => {
            if (localValue !== item.content) onChange(item.key, localValue)
          }}
        />
        {level < 2 && (
          <span className="add-item-btn" onClick={() => onAdd(item.key)}>
            <PlusOutlined style={{ marginRight: '4px' }} />
            子选项
          </span>
        )}
      </div>
      {(level > 0 || !isOnlyOne) && (
        <div className="cascade-item-actions">
          <CloseOutlined
            className="close-btn"
            onClick={() => onDelete(item.key)}
          />
        </div>
      )}
    </div>
  )
}

const insertKey = (data: ItemNode[], parentKey = '') => {
  return data.map((item, index) => {
    if (item.childs && item.childs.length > 0) {
      return {
        ...item,
        key: parentKey ? `${parentKey}-${index}` : String(index),
        childs: insertKey(
          item.childs,
          parentKey ? `${parentKey}-${index}` : String(index)
        )
      }
    }
    return {
      ...item,
      key: parentKey ? `${parentKey}-${index}` : String(index)
    }
  })
}

interface ItemTreeProps {
  itemID: string | number
  clickEvent: (clickItem: string) => void
}

const ItemTree: React.FC<ItemTreeProps> = ({ itemID, clickEvent }) => {
  const dispatch = useAppDispatch()
  const [expandedKeys, setExpandedKeys] = useState<string[]>([])
  const allCascadeItems = useAppSelector((state) => state.editor.cascadeItems)

  const cascadeItems = useMemo(() => {
    if (allCascadeItems.length > 0) {
      const itemCascadeItems = allCascadeItems.find(
        (item) => item.itemId === itemID
      )
      if (itemCascadeItems) {
        // 深拷贝一份再用，避免直接复用 RTK/Immer 冻结的 records/childs：
        // 下方 onAddItem/onDeleteItem/onDrop 均对 childs 做就地 push/splice，
        // 若 childs 为 store 中被 Immer 冻结的数组会抛 "object is not extensible"
        // 导致「添加子选项点击无响应」（对齐基准 origin/master 的 .toJS() 深拷贝语义）
        const records: ItemNode[] = JSON.parse(
          JSON.stringify(itemCascadeItems.records || [])
        )
        return insertKey(records)
      }
    }
    return []
  }, [allCascadeItems, itemID])

  useEffect(() => {
    const keys: string[] = []
    const loop = (data: ItemNode[]) => {
      data.forEach((item) => {
        keys.push(item?.key || '')
        if (item.childs) {
          loop(item.childs)
        }
      })
    }
    loop(cascadeItems)
    setExpandedKeys(keys)
  }, [])

  const onChangeCascadeItems = (newRecords: ItemNode[]) => {
    const index = allCascadeItems.findIndex((item) => item.itemId === itemID)
    if (index === -1) return
    const newCascadeItems = allCascadeItems.map((item, idx) =>
      idx === index ? { ...item, records: newRecords } : item
    )
    dispatch(setEditorCascadeItemsList(newCascadeItems))
  }

  const loop = (data: ItemNode[], key: string, callback) => {
    data.forEach((item, index, arr) => {
      if (item.key === key) {
        return callback(item, index, arr)
      }
      if (item.childs) {
        return loop(item.childs, key, callback)
      }
    })
  }

  const onAddItem = (key: string) => {
    const data = [...cascadeItems]
    loop(data, key, (item: ItemNode) => {
      item.childs = item.childs || []
      item.childs.push({
        key: `${key}-${item.childs.length}`,
        content: ''
      })
    })
    onChangeCascadeItems(data)
    setExpandedKeys([...expandedKeys, key])
    clickEvent('addSuboption')
  }

  const onChangeItem = (key: string, value: string) => {
    const data = [...cascadeItems]
    loop(data, key, (item: ItemNode) => {
      item.content = value
    })
    onChangeCascadeItems(data)
  }

  const onDeleteItem = (key: string) => {
    const data = [...cascadeItems]
    let hasChildren = false
    loop(data, key, (item: ItemNode, index: number, arr: ItemNode[]) => {
      if (item.childs && item.childs.length > 0) {
        hasChildren = true
      }
      arr.splice(index, 1)
    })
    if (hasChildren) {
      Modal.confirm({
        title: '删除选项',
        content: '是否删除当前选项及其子选项所有内容？',
        okText: '删除',
        onOk: () => onChangeCascadeItems(data)
      })
    } else {
      onChangeCascadeItems(data)
    }
  }

  const createItems = (data: ItemNode[], level = 0) => {
    const isOnlyOne = data.length === 1
    return data.map((item, index) => {
      if (item.childs && item.childs.length > 0) {
        return (
          <TreeNode
            title={
              <ContentItem
                item={item}
                level={level}
                isOnlyOne={isOnlyOne}
                onChange={onChangeItem}
                onAdd={onAddItem}
                onDelete={onDeleteItem}
              />
            }
            key={item.key}
          >
            {createItems(item.childs, level + 1)}
          </TreeNode>
        )
      }
      return (
        <TreeNode
          title={
            <ContentItem
              item={item}
              level={level}
              onChange={onChangeItem}
              onAdd={onAddItem}
              onDelete={onDeleteItem}
              isOnlyOne={isOnlyOne}
            />
          }
          key={item.key}
        />
      )
    })
  }

  const onDrop = useCallback(
    (info) => {
      const dropKey = info.node.props.eventKey
      const dragKey = info.dragNode.props.eventKey
      const dropPos = info.node.props.pos.split('-')
      const dropPosition =
        info.dropPosition - Number(dropPos[dropPos.length - 1])

      const data = [...cascadeItems]

      // Find dragObject
      let dragObj: ItemNode
      loop(data, dragKey, (item: ItemNode, index: number, arr: ItemNode[]) => {
        arr.splice(index, 1)
        dragObj = item
      })

      if (!info.dropToGap) {
        // Drop on the content
        loop(data, dropKey, (item: ItemNode) => {
          item.childs = item.childs || []
          // where to insert 示例添加到尾部，可以是随意位置
          item.childs.push(dragObj)
        })
      } else if (
        (info.node.props.childs || []).length > 0 && // Has children
        info.node.props.expanded && // Is expanded
        dropPosition === 1 // On the bottom gap
      ) {
        loop(data, dropKey, (item: ItemNode) => {
          item.childs = item.childs || []
          // where to insert 示例添加到头部，可以是随意位置
          item.childs.unshift(dragObj)
        })
      } else {
        let ar: ItemNode[] = []
        let i = 0
        loop(
          data,
          dropKey,
          (item: ItemNode, index: number, arr: ItemNode[]) => {
            ar = arr
            i = index
          }
        )
        if (dropPosition === -1) {
          ar.splice(i, 0, dragObj!)
        } else {
          ar.splice(i + 1, 0, dragObj!)
        }
      }

      onChangeCascadeItems(data)
    },
    [cascadeItems]
  )

  return (
    <Tree
      className="cascade-items-tree"
      // antd6 的 draggable 默认会渲染内置 holder 拖动图标（≡），
      // 基准 antd3 无此图标；用 { icon: false } 关闭它，保留拖拽能力、不显示手柄
      draggable={{ icon: false }}
      blockNode
      expandedKeys={expandedKeys}
      onExpand={(keys) => setExpandedKeys(keys as string[])}
      onDrop={onDrop}
    >
      {createItems(cascadeItems)}
    </Tree>
  )
}

export default ItemTree
